import React from "react";
import { createPortal } from "react-dom";
import { Box, Chip } from "@mui/joy";
import {
  colorTokens,
  ctaAccentColor,
  ctaAccentHoverColor,
  ctaAccentTextColor,
  textTokens,
} from "../../../../styles/globalStyles";

interface LimitedChipsTooltipProps<T> {
  items: T[];
  limit?: number;
  dynamicLimit?: boolean;
  renderChip: (item: T, index: number) => React.ReactNode;
  renderTooltipItem: (item: T, index: number) => React.ReactNode;
  direction?: "row" | "column";
}

export default function LimitedChipsTooltip<T>({
  items,
  limit = 5,
  dynamicLimit = false,
  renderChip,
  renderTooltipItem,
  direction = "row",
}: LimitedChipsTooltipProps<T>) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipConfig, setTooltipConfig] = React.useState({
    x: 0,
    y: 0,
    showAbove: true,
    width: 200,
  });
  const [calculatedLimit, setCalculatedLimit] = React.useState(limit);
  const chipRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const screenWidth = window.innerWidth;
  const isMobile = screenWidth < 768;
  const isSmallMobile = screenWidth < 480;

  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: ${isMobile ? "8px" : "6px"};
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
        margin: 4px 0;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: ${isMobile ? ctaAccentColor : textTokens.color.dimmed};
        border-radius: 4px;
        border: ${isMobile ? "1px solid rgba(0,0,0,0.2)" : "none"};
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background-color: ${isMobile ? ctaAccentHoverColor : textTokens.color.faint};
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isMobile]);

  const chipFontSize = isSmallMobile ? "0.65rem" : isMobile ? "0.7rem" : "0.75rem";
  const tooltipFontSize = isSmallMobile ? "0.7rem" : isMobile ? "0.75rem" : "0.8rem";

  React.useEffect(() => {
    if (dynamicLimit && containerRef.current && items && items.length > 0) {
      const containerWidth = containerRef.current.getBoundingClientRect().width;
      const estimatedChipWidth = isSmallMobile ? 50 : isMobile ? 60 : 70;
      const plusChipWidth = isSmallMobile ? 35 : isMobile ? 40 : 45;
      const gapWidth = 4;

      const availableWidth = containerWidth - plusChipWidth - gapWidth;
      const maxChips = Math.floor((availableWidth + gapWidth) / (estimatedChipWidth + gapWidth));
      const newLimit = Math.max(1, Math.min(maxChips, items.length - 1));
      setCalculatedLimit(newLimit);
    } else {
      setCalculatedLimit(limit);
    }
  }, [dynamicLimit, limit, items, isSmallMobile, isMobile]);

  const activeLimit = dynamicLimit ? calculatedLimit : limit;
  const calculateMaxHeight = () => {
    const itemsInTooltip = isMobile ? items.length : Math.max(0, items.length - activeLimit);

    if (isMobile) {
      return isSmallMobile ? "400px" : "450px";
    }

    const heightPerItem = 35;
    const padding = 20;
    const estimatedHeight = itemsInTooltip * heightPerItem + padding;
    const baseHeight = 400;
    const maxScreenHeight = window.innerHeight * 0.8;

    return Math.min(Math.max(baseHeight, estimatedHeight), maxScreenHeight) + "px";
  };

  const tooltipMaxHeight = calculateMaxHeight();

  if (!items || items.length === 0) return null;
  const displayedItems = isMobile ? [] : items.slice(0, activeLimit);
  const remainingCount = isMobile ? items.length : items.length - activeLimit;
  const hasMore = isMobile ? items.length > 0 : remainingCount > 0;
  const tooltipItems = isMobile ? items : items.slice(activeLimit);

  const handleMouseEnter = () => {
    if (chipRef.current) {
      const rect = chipRef.current.getBoundingClientRect();
      const tooltipHeight = parseInt(tooltipMaxHeight);

      const screenWidth = window.innerWidth;
      let tooltipWidth;
      if (isSmallMobile) {
        tooltipWidth = screenWidth * 0.95;
      } else if (isMobile) {
        tooltipWidth = Math.min(screenWidth * 0.9, 350);
      } else {
        tooltipWidth = Math.min(500, screenWidth * 0.6);
      }
      const margin = 8;

      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceAbove >= spaceBelow;

      let tooltipX = rect.left + rect.width / 2;
      const halfTooltipWidth = tooltipWidth / 2;

      if (tooltipX + halfTooltipWidth > window.innerWidth - margin) {
        tooltipX = window.innerWidth - halfTooltipWidth - margin;
      }

      if (tooltipX - halfTooltipWidth < margin) {
        tooltipX = halfTooltipWidth + margin;
      }

      setTooltipConfig({
        x: tooltipX,
        y: showAbove ? rect.top - tooltipHeight - margin : rect.bottom + margin,
        showAbove,
        width: tooltipWidth,
      });
    }
    setShowTooltip(true);
  };

  return (
    <Box
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
      sx={{
        display: "flex",
        flexDirection: direction,
        flexWrap: direction === "row" ? "wrap" : "nowrap",
        gap: direction === "row" ? 0.5 : 1.5,
        alignItems: "center",
        width: "100%",
      }}
    >
      {displayedItems.map((item, index) => (
        <div
          key={index}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={(e) => e.stopPropagation()}
          onMouseLeave={(e) => e.stopPropagation()}
        >
          {renderChip(item, index)}
        </div>
      ))}
      {hasMore && (
        <>
          <Chip
            ref={chipRef}
            size="sm"
            variant="soft"
            onClick={(e) => {
              e.stopPropagation();
              if (isMobile) {
                if (chipRef.current) {
                  const rect = chipRef.current.getBoundingClientRect();
                  setTooltipConfig({
                    x: window.innerWidth / 2,
                    y: Math.min(rect.top - 20, window.innerHeight / 2 - 150),
                    showAbove: true,
                    width: isSmallMobile
                      ? window.innerWidth * 0.95
                      : Math.min(window.innerWidth * 0.9, 350),
                  });
                }
                setShowTooltip(!showTooltip);
              }
            }}
            onMouseEnter={!isMobile ? handleMouseEnter : undefined}
            onMouseLeave={!isMobile ? () => setShowTooltip(false) : undefined}
            sx={{
              backgroundColor: ctaAccentColor,
              color: ctaAccentTextColor,
              fontSize: chipFontSize,
              fontWeight: 600,
              cursor: "pointer",
              mt: direction === "column" ? 1 : 0,
              px: isSmallMobile ? 0.5 : 1,
              py: isSmallMobile ? 0.25 : 0.5,
              "&:hover": { backgroundColor: ctaAccentHoverColor },
            }}
          >
            +{remainingCount}
          </Chip>
          {showTooltip &&
            createPortal(
              <>
                {isMobile && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.3)",
                      zIndex: 9998,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTooltip(false);
                    }}
                  />
                )}
                <div
                  style={{
                    position: "fixed",
                    left: tooltipConfig.x,
                    top: tooltipConfig.y,
                    transform: "translateX(-50%)",
                    backgroundColor: colorTokens.bg.overlay,
                    padding: isMobile ? "12px" : "8px",
                    borderRadius: isMobile ? "8px" : "6px",
                    width: `${tooltipConfig.width}px`,
                    maxHeight: tooltipMaxHeight,
                    overflowY: "auto",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
                    color: textTokens.color.primary,
                    zIndex: 9999,
                    border: "none",
                    outline: "none",
                    pointerEvents: isMobile ? "auto" : "none",
                    scrollbarWidth: "thin",
                    scrollbarColor: isMobile
                      ? `${ctaAccentColor} rgba(255,255,255,0.1)`
                      : "#666 transparent",
                  }}
                  className="custom-scrollbar"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      display: isMobile ? "flex" : "grid",
                      flexDirection: isMobile ? "column" : undefined,
                      gridTemplateColumns: isMobile
                        ? undefined
                        : `repeat(auto-fit, minmax(160px, 1fr))`,
                      gap: isMobile ? "8px" : "4px",
                      justifyItems: isMobile ? undefined : "start",
                      paddingBottom: "0",
                      position: "relative",
                    }}
                  >
                    {tooltipItems.map((item, index) => (
                      <div
                        key={index}
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => e.stopPropagation()}
                        onMouseLeave={(e) => e.stopPropagation()}
                        style={{
                          fontSize: tooltipFontSize,
                          width: isMobile ? "100%" : "auto",
                          display: "flex",
                          justifyContent: isMobile ? "center" : "flex-start",
                        }}
                      >
                        {renderTooltipItem(item, index)}
                      </div>
                    ))}
                  </div>
                </div>
              </>,
              document.body,
            )}
        </>
      )}
    </Box>
  );
}
